/**
 * AcceptCoachInvitation Page
 * Allows users to accept or decline admin invitations to become a coach
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { GraduationCap, Check, X, AlertTriangle, Clock, ArrowRight } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useAuthStore } from '../stores/authStore'
import { createLogger } from '../utils/logger'

const logger = createLogger('AcceptCoachInvitation')

const AcceptCoachInvitation = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { checkAuth } = useAuthStore()

  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [invitation, setInvitation] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await api.get(`/user/coach-invitation/${token}`)
        if (response.data.success) {
          setInvitation(response.data.invitation)
        } else {
          setError(response.data.error || 'Invalid invitation')
        }
      } catch (err) {
        logger.error('Error fetching invitation:', err)
        if (err.response?.status === 404) {
          setError('This invitation is invalid or has expired.')
        } else if (err.response?.status === 403) {
          setError('This invitation is not for your account.')
        } else {
          setError(err.response?.data?.error || 'Failed to load invitation details')
        }
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      fetchInvitation()
    }
  }, [token])

  const handleAccept = async () => {
    setActionLoading(true)
    try {
      const response = await api.post(`/user/accept-coach-invitation/${token}`)
      if (response.data.success) {
        setSuccess(true)
        toast.success('Congratulations! You are now a coach!')
        // Refresh user data to get updated isCoach status
        await checkAuth()
        // Redirect to coach dashboard after a short delay
        setTimeout(() => {
          navigate('/coach')
        }, 2000)
      }
    } catch (err) {
      logger.error('Error accepting invitation:', err)
      toast.error(err.response?.data?.error || 'Failed to accept invitation')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDecline = async () => {
    setActionLoading(true)
    try {
      const response = await api.post(`/user/decline-coach-invitation/${token}`)
      if (response.data.success) {
        toast.success('Invitation declined')
        navigate('/dashboard')
      }
    } catch (err) {
      logger.error('Error declining invitation:', err)
      toast.error(err.response?.data?.error || 'Failed to decline invitation')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Invitation Error
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link to="/dashboard" className="btn btn-primary">
            Go to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Welcome to the Coaching Team!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You are now a coach. Redirecting you to your coach dashboard...
          </p>
          <div className="flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400">
            <LoadingSpinner size="sm" />
            <span>Redirecting...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              You've Been Invited to Become a Coach!
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              You've been selected to join the coaching team on Deaveris.
            </p>
          </div>

          {/* Invitation Details */}
          {invitation?.expiresAt && (
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-primary-700 dark:text-primary-300">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  This invitation expires on{' '}
                  {new Date(invitation.expiresAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
          )}

          {/* What you can do as a coach */}
          <div className="mb-8">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
              As a coach, you'll be able to:
            </h3>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                <span>Invite students and help them on their learning journey</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                <span>Track student progress with detailed analytics</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                <span>Assign challenges and provide guidance</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                <span>Message students and send announcements</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              disabled={actionLoading}
              className="btn btn-secondary flex-1 inline-flex items-center justify-center gap-2"
            >
              {actionLoading ? <LoadingSpinner size="xs" /> : <X className="w-4 h-4" />}
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={actionLoading}
              className="btn btn-primary flex-1 inline-flex items-center justify-center gap-2"
            >
              {actionLoading ? <LoadingSpinner size="xs" /> : <Check className="w-4 h-4" />}
              Accept Invitation
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AcceptCoachInvitation
